import React, { useCallback, useMemo } from "react";
import moment from "moment-timezone";

import { OutputRow as AvailabilityData } from "api/getAvailability";
import { ValidationCallback } from "validation/validate";
import ModalFieldHourOfDay from "components/ModalFieldHourOfDay";
import { OnCompleteOutput } from "components/ModalWizard/ModalWizard";
import EditableTable from "components/EditableTable";
import { columnDefinitionTimezone } from "components/EditableTable/columnDefinitionTimezone";
import { useStickySort } from "hooks/useStickySort";
import StepDaysOfWeek from "./NewRowWizard/StepDaysOfWeek";
import StepTimeFrom from "./NewRowWizard/StepTimeFrom";
import StepTimeTo from "./NewRowWizard/StepTimeTo";
import StepTimezone from "components/PageDashboard/StepTimezone";
import { useAddActionButtonDelete } from "hooks/useAddActionButtonDelete";
import { useHandleOKTimeOfDayField } from "hooks/useHandleOKTimeOfDayField";

export type DataInAlreadyExistingRow = AvailabilityData;

export interface DataInFreshlyEnteredRow
	extends Omit<DataInAlreadyExistingRow, "dayOfWeekLocaltime"> {
	dayOfWeekLocaltime: DataInAlreadyExistingRow["dayOfWeekLocaltime"][];
}

interface AvailabilityTableProps {
	periodsOfAvailability: DataInAlreadyExistingRow[];
	isLoading: boolean;
	isWaiting: boolean;
	isError: boolean;
	waitModalTitle: string;
	waitModalIsShowing: boolean;
	onEdit: (
		rows: { old?: DataInAlreadyExistingRow; new?: DataInAlreadyExistingRow }[]
	) => OnCompleteOutput;
}

const AvailabilityTable: React.FunctionComponent<AvailabilityTableProps> = ({
	periodsOfAvailability,
	onEdit: onEditGlobal,
	isLoading,
	isWaiting,
	isError,
	waitModalTitle,
	waitModalIsShowing
}) => {
	const onNewRowWizardComplete = useCallback(
		(data: DataInFreshlyEnteredRow) => {
			if (!data.dayOfWeekLocaltime) {
				let hasBeenAborted = false;
				return {
					ready: Promise.resolve(),
					abort: () => {
						hasBeenAborted = true;
					},
					aborted: () => hasBeenAborted
				};
			}
			return onEditGlobal(
				data.dayOfWeekLocaltime.map(dayOfWeekLocaltime => {
					return {
						new: {
							...data,
							dayOfWeekLocaltime
						}
					};
				})
			);
		},
		[onEditGlobal]
	);

	const sortedAvailabilityPeriods = useStickySort(
		periodsOfAvailability,
		useCallback((a: DataInAlreadyExistingRow, b: DataInAlreadyExistingRow) => {
			if (a.dayOfWeekLocaltime !== b.dayOfWeekLocaltime) {
				return a.dayOfWeekLocaltime - b.dayOfWeekLocaltime;
			}

			const aStart = moment
				.duration(a.timeFirstAvailableLocaltime)
				.as("milliseconds");
			const bStart = moment
				.duration(b.timeFirstAvailableLocaltime)
				.as("milliseconds");

			if (aStart !== bStart) {
				return aStart - bStart;
			}

			const aEnd = moment
				.duration(a.timeLastAvailableJustBeforeLocaltime)
				.as("milliseconds");
			const bEnd = moment
				.duration(b.timeLastAvailableJustBeforeLocaltime)
				.as("milliseconds");

			return aEnd - bEnd;
		}, [])
	);

	const rows = useAddActionButtonDelete(
		sortedAvailabilityPeriods,
		onEditGlobal
	);

	const newRowWizard = useMemo(() => {
		const steps = [
			{
				key: "timezone",
				title: "What timezone will you set the times in?",
				shortTitle: "Time zone",
				component: StepTimezone
			},
			{
				key: "days-of-week",
				title: "What days of the week?",
				shortTitle: "Days of the week",
				component: StepDaysOfWeek
			},
			{
				key: "time-from",
				title: "What time are you available from?",
				shortTitle: "Time from",
				component: StepTimeFrom,
				doScrollToInitialPosition: true
			},
			{
				key: "time-to",
				title: "What time are you available until?",
				shortTitle: "Time until",
				component: StepTimeTo,
				doScrollToInitialPosition: true
			}
		];

		const validateNewRowData: ValidationCallback<DataInFreshlyEnteredRow> = (
			input,
			{ flag, checkField }
		) => {
			const {
				dayOfWeekLocaltime,
				timeFirstAvailableLocaltime,
				timeLastAvailableJustBeforeLocaltime
			} = input;
			checkField("timezone", { type: "string" });

			if (!dayOfWeekLocaltime) {
				flag("dayOfWeekLocaltime", "mising");
			} else if (!Array.isArray(dayOfWeekLocaltime)) {
				flag("dayOfWeekLocaltime", "not an array");
			} else {
				for (const day of dayOfWeekLocaltime) {
					if (typeof day !== "number") {
						flag("dayOfWeekLocaltime", "not all numbers");
					}

					if (parseInt(day + "") !== day) {
						flag("dayOfWeekLocaltime", "not all integers");
					}

					if (day < 1 || day > 7) {
						flag("dayOfWeekLocaltime", "not all in range");
					}
				}
			}

			if (!timeFirstAvailableLocaltime) {
				flag("timeFirstAvailableLocaltime", "mising");
			} else if (!moment.isDuration(timeFirstAvailableLocaltime)) {
				flag("timeFirstAvailableLocaltime", "not a moment duration");
			}

			if (!timeLastAvailableJustBeforeLocaltime) {
				flag("timeLastAvailableJustBeforeLocaltime", "mising");
			} else if (!moment.isDuration(timeLastAvailableJustBeforeLocaltime)) {
				flag("timeLastAvailableJustBeforeLocaltime", "not a moment duration");
			}
		};

		const initialData = {};

		return {
			steps,
			validateNewRowData,
			initialData
		};
	}, []);

	const handleOKTimeOfDayField = useHandleOKTimeOfDayField();

	return (
		<EditableTable<DataInAlreadyExistingRow, DataInFreshlyEnteredRow>
			allowDeleteRows
			onEdit={onEditGlobal}
			dataDescriptionSingular="availability period"
			dataDescriptionPlural="availability periods"
			onNew={onNewRowWizardComplete}
			{...{
				isLoading,
				isWaiting,
				isError,
				waitModalTitle,
				waitModalIsShowing,
				rows,
				newRowWizard
			}}
			cols={[
				columnDefinitionTimezone({ initialValue: row => row.timezone }),
				{
					key: "timeFirstAvailableLocaltime",
					heading: "From",
					field: ({ row, rowKey, isEditing, onEdit }) => (
						<ModalFieldHourOfDay
							isUnlocked={isEditing}
							title="Available from"
							value={row.timeFirstAvailableLocaltime.hours()}
							requireSelectedTime
							isDisabled={(value: number | undefined) => {
								return row.timeLastAvailableJustBeforeLocaltime === undefined
									? false
									: value === undefined
									? false
									: value >= row.timeLastAvailableJustBeforeLocaltime.hours();
							}}
							onOK={(newValue: number | undefined) => {
								handleOKTimeOfDayField(row, newValue, rowKey, onEdit);
							}}
						/>
					)
				},
				{
					key: "timeLastAvailableJustBeforeLocaltime",
					heading: "Until",
					field: ({ row, rowKey, isEditing, onEdit }) => (
						<ModalFieldHourOfDay
							isUnlocked={isEditing}
							title="Available until"
							value={row.timeLastAvailableJustBeforeLocaltime.hours()}
							requireSelectedTime
							isDisabled={(value: number | undefined) =>
								row.timeFirstAvailableLocaltime === undefined
									? false
									: value === undefined
									? false
									: value <= row.timeFirstAvailableLocaltime.hours()
							}
							onOK={(newValue: number | undefined) => {
								handleOKTimeOfDayField(row, newValue, rowKey, onEdit);
							}}
						/>
					)
				}
			]}
		/>
	);
};

export default AvailabilityTable;
